import { useQuery } from '@tanstack/react-query';
import type { Profile } from '@nelnela/contracts';
import { api } from './api';

/**
 * The learner's profile as it is NOW, not as it was at boot.
 *
 * `useStudy().me` is deliberately a boot snapshot: `StudyProvider` memoizes its context on
 * `[profileId, entitled]` so that a background `/api/me` refetch cannot rebuild the event queue
 * mid-flush (see the comment in `app/study.tsx`). That is exactly right for the queue and
 * exactly wrong for settings — a value written on Me would otherwise never reach Watch, and the
 * control that wrote it would snap back to the old value the moment the mutation settled.
 *
 * So read the same `['me']` query the session gate already populates. No extra request (same
 * cache entry), and react-query's structural sharing means an unchanged refetch does not even
 * re-render. The `fallback` is the boot snapshot, for the frame before the cache is warm.
 */
export function useLiveProfile(fallback: Profile | null): Profile | null {
  const q = useQuery({ queryKey: ['me'], queryFn: api.me });
  return q.data?.profiles[0] ?? fallback;
}
